import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import * as echarts from 'echarts';
import 'echarts-for-react';
import ReactECharts from 'echarts-for-react';
import api from '../api/axios';
import 'react-datepicker/dist/react-datepicker.css';
import BLUE_SHADES, { getBlueShade } from '../constants/chartColors';
import * as moment from "moment-timezone"
import { useTranslation } from 'react-i18next';
import Table from '../components/Table';

function MetricCard({
  title,
  value,
  subtitle,
  color = 'text-white',
  percentage,
  progressColor = 'bg-gradient-to-r from-blue-400 to-blue-800',
  showPercentage = false,
}) {
  const hasProgress = typeof percentage === 'number' && percentage >= 0 && percentage <= 100;
  console.log("percentage ", percentage, typeof percentage);

  percentage = Math.min(100, percentage);
  value = typeof value == 'number' ? Number(value || 0).toFixed(1) : value;

  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (hasProgress) {
      setTimeout(() => {
        setWidth(percentage);
      }, 50); // small delay triggers transition
    }
  }, [percentage]);

  return (
    <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow hover:shadow-lg transition relative overflow-hidden">
      {hasProgress && (
        <div
          className={`absolute inset-y-0 left-0 ${progressColor} opacity-20 transition-all duration-1000 ease-out`}
          style={{ width: `${width}%` }}
        />
      )}

      <div className="relative z-10">
        <div className="text-white-400 text-sm font-medium">{title}</div>

        <div className="flex items-baseline justify-between mt-2">
          <div className={`text-2xl sm:text-3xl font-bold ${color}`}>
            {value}
          </div>

          {hasProgress && showPercentage && (
            <div
              className="absolute text-2xl sm:text-3xl lg:text-7xl font-black tracking-tight"
              style={{
                color: "#94a3b8",
                right: 0,
                top: 0,
                opacity: "20%"
              }}
            >
              {Math.round(percentage)}%
            </div>
          )}
        </div>

        {subtitle && (
          <div className="text-gray-500 text-sm mt-1">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

function DailySummaryTable({ data }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  return (
    <div className="bg-gray-800 rounded-2xl shadow overflow-hidden">
      <h2 className="text-lg sm:text-xl p-4 sm:p-5 font-semibold border-b border-gray-700">{t('daily_breakdown')}</h2>
      <Table
        data={data}
        columns={[
          { header: t('date'), accessor: 'date', sortable: true, className: "font-medium whitespace-nowrap" },
          { header: t('present'), accessor: 'attendance', sortable: true, align: 'right' },
          { header: t('worked_h'), accessor: 'hours', sortable: true, align: 'right' },
          { header: t('expected_h'), accessor: 'expected', sortable: true, align: 'right' },
          { header: t('due_h'), accessor: 'due', sortable: true, align: 'right', className: 'text-orange-400', render: (row) => Number(row.due).toFixed(2) },
          { header: t('overtime_h'), accessor: 'overtime', sortable: true, align: 'right', className: 'text-yellow-400', render: (row) => Number(row.overtime).toFixed(2) },
          { header: t('avg_h_person'), accessor: 'avgHours', sortable: true, align: 'right', render: (row) => row.avgHours || '—' },
          { header: t('attendance_pct'), accessor: 'attendancePct', sortable: true, align: 'right', render: (row) => `${row.attendancePct}%` }
        ]}
        defaultSortColumn={0}
        keyExtractor={(row) => row.date}
        containerClassName="max-h-[480px] overflow-auto"
        headerClassName="bg-gray-700 sticky top-0 z-10"
      />
    </div>
  );
}

export default function DailySummaryPage() {
  const today = new Date();
  const defaultEnd = today;
  const defaultStart = new Date(today);
  defaultStart.setDate(today.getDate() - 6);

  const [dateRange, setDateRange] = useState([defaultStart, defaultEnd]);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { t } = useTranslation();

  const handleDateRange = ([start, end]) => {
    setDateRange([start, end]);
  }

  const [metrics, setMetrics] = useState({
    totalHours: 0,
    avgHoursPerDay: 0,
    attendanceRateAvg: '—',
    totalOvertime: 0,
    attendanceRateAvgValue: 0,
    totalDue: 0,
  });

  const maxRangeDays = 30;

  useEffect(() => {
    if (dateRange[0]) setStartDate(dateRange[0]);
    if (dateRange[1]) setEndDate(dateRange[1]);
  }, [dateRange]);

  const fetchData = async () => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) {
      setError(t('start_date_after_end'));
      return;
    }

    const diffDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > maxRangeDays) {
      setError(t('max_range_days', { maxRangeDays }));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const companyId = localStorage.getItem("companyId");
      if (!companyId) throw new Error(t('company_id_not_found'));

      const start = moment.tz(startDate, "Asia/Tashkent").utc().toISOString();
      const end = moment.tz(endDate, "Asia/Tashkent").endOf('D').utc().toISOString();

      const res = await api.get(
        `/metrics/${companyId}/daily?startDate=${start}&endDate=${end}`
      );

      const data = res.data.data || [];
      setDailyData(data);

      if (data.length > 0) {
        const totalHours = data.reduce((s, d) => s + (d.totalHours || 0), 0);
        const averageHours = data.reduce((s, d) => s + (d.averageHours || 0), 0);
        setMetrics({
          totalHours,
          avgHoursPerDay: (averageHours / data.length).toFixed(1),
          attendanceRateAvg: (data.reduce((s, d) => s + (d.attendancePercentage || 0), 0) / data.length).toFixed(1) + '%',
          attendanceRateAvgValue: data.reduce((s, d) => s + (d.attendancePercentage || 0), 0) / data.length,
          totalOvertime: data.reduce((s, d) => s + (d.totalOverTimeHours || 0), 0),
          totalDue: data.reduce((s, d) => s + (d.totalDueHours || 0), 0),
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || t('failed_to_load_data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const chartData = dailyData
    .map(day => ({
      date: day.DateLocal || '—',
      attendance: day.attendanceCount || 0,
      hours: day.totalHours || 0,
      expected: day.expectedWorkingHours || 0,
      due: Number(day.totalDueHours || 0).toFixed(2),
      overtime: Number(day.totalOverTimeHours || 0).toFixed(2),
      avgHours: day.attendanceCount > 0 ? Number((day.totalHours / day.attendanceCount).toFixed(1)) : 0,
      attendancePct: Number((day.attendancePercentage || 0).toFixed(1)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const dates = chartData.map(d => d.date);

  const getChartWidth = (dataLength) => {
    if (dataLength <= 7) return '100%';
    return `${Math.max(700, dataLength * 130)}px`;
  };

  const commonOptions = {
    tooltip: { trigger: 'axis' },
    grid: { left: '4%', right: '4%', bottom: '12%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { color: '#aaa', rotate: dates.length > 10 ? 45 : 0, interval: 0 },
      axisLine: { lineStyle: { color: '#444' } }
    },
  };

  return (
    <div className="p-4 sm:p-6 text-white space-y-6 sm:space-y-8 min-h-screen bg-gray-900">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-400">{t('daily_summary')}</h1>

        <div className="flex flex-wrap items-end gap-4" style={{ zIndex: 9999 }}>
          <div>
            <label className="block text-sm text-gray-400 mb-1">{t('date_range')}</label>
            <DatePicker
              selectsRange={true}
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={(update) => handleDateRange(update)}
              className="bg-gray-800 border border-gray-700 rounded px-4 py-2.5 w-full sm:w-64 text-white cursor-pointer"
              placeholderText={t('select_date_range')}
              dateFormat="yyyy-MM-dd"
              showMonthDropdown
              showYearDropdown
              locale="ru"
              dropdownMode="select"
            />
          </div>

          <button
            onClick={fetchData}
            disabled={loading}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-200 shadow-lg ${loading 
              ? 'bg-gray-700 cursor-not-allowed text-gray-400' 
              : 'bg-sky-600 hover:bg-sky-500 text-white shadow-sky-900/20 active:scale-95'
              }`}
          >
            {loading ? t('loading') : t('apply')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/60 border border-red-700 text-red-100 px-5 py-3 rounded-xl">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 min-w-[900px]">
          <MetricCard title={t('total_hours')} value={metrics.totalHours} subtitle={t('selected_period')} />
          <MetricCard title={t('avg_hours_day')} value={metrics.avgHoursPerDay} subtitle={t('per_day')} />
          <MetricCard title={t('attendance_rate')} percentage={metrics.attendanceRateAvgValue} value={metrics.attendanceRateAvg} subtitle={t('average')} color="text-blue-400" />
          <MetricCard title={t('overtime_hours')} value={metrics.totalOvertime} subtitle={t('total')} color="text-blue-400" />
          <MetricCard title={t('due_missing_hours')} value={metrics.totalDue} subtitle={t('total')} color="text-blue-400" />
        </div>
      </div>

      <div className="space-y-8 max-h-[calc(100vh-380px)] overflow-auto pb-6">
        <div className="grid lg:grid-cols-2 gap-5 sm:gap-8">
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow overflow-x-auto">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('attendance_avg_hours')}</h2>
            <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
              <ReactECharts
                option={{
                  ...commonOptions,
                  yAxis: [
                    { type: 'value', name: t('present'), nameTextStyle: { color: '#aaa' }, axisLine: { lineStyle: { color: '#3b82f6' } } },
                    { type: 'value', name: t('avg_h_person'), nameTextStyle: { color: '#aaa' }, position: 'right', axisLine: { lineStyle: { color: '#10b981' } } }
                  ],
                  series: [
                    {
                      name: t('present'),
                      type: 'line',
                      yAxisIndex: 0,
                      data: chartData.map(d => d.attendance),
                      itemStyle: { color: '#3b82f6' },
                      lineStyle: { width: 2 }
                    },
                    {
                      name: t('avg_h_person'),
                      type: 'line',
                      yAxisIndex: 1,
                      data: chartData.map(d => d.avgHours),
                      itemStyle: { color: '#10b981' },
                      lineStyle: { width: 2 }
                    }
                  ]
                }}
                style={{ height: 320, minWidth: '100%' }}
              />
            </div>
          </div>

          {/* Chart 2 */}
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('total_hours_worked')}</h2>
            <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
              <ReactECharts
                option={{
                  ...commonOptions,
                  yAxis: { type: 'value', axisLabel: { color: '#aaa' }, axisLine: { lineStyle: { color: '#444' } } },
                  series: [{
                    name: t('worked_h'),
                    type: 'bar',
                    data: chartData.map(d => d.hours),
                    itemStyle: { color: BLUE_SHADES.dark3 },
                    barMaxWidth: 40
                  }]
                }}
                style={{ height: 320, minWidth: '100%' }}
              />
            </div>
          </div>

          {/* Chart 3 */}
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('daily_attendance_rate')}</h2>
            <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
              <ReactECharts
                option={{
                  ...commonOptions,
                  yAxis: {
                    type: 'value',
                    min: 0,
                    max: 100,
                    axisLabel: { formatter: '{value} %', color: '#aaa' },
                    axisLine: { lineStyle: { color: '#444' } }
                  },
                  series: [{
                    name: t('attendance_pct'),
                    type: 'line',
                    data: chartData.map(d => d.attendancePct),
                    itemStyle: { color: getBlueShade(0) },
                    lineStyle: { width: 2 }
                  }]
                }}
                style={{ height: 320, minWidth: '100%' }}
              />
            </div>
          </div>

          {/* Chart 4 */}
          <div className="bg-gray-800 rounded-2xl p-4 sm:p-5 shadow">
            <h2 className="text-lg sm:text-xl mb-3 sm:mb-4 font-semibold">{t('overtime_due_hours')}</h2>
            <div className="overflow-x-auto" style={{ minWidth: '100%' }}>
              <ReactECharts
                option={{
                  ...commonOptions,
                  legend: { textStyle: { color: '#ddd' } },
                  yAxis: { type: 'value', axisLabel: { color: '#aaa' }, axisLine: { lineStyle: { color: '#444' } } },
                  series: [
                    {
                      name: t('overtime_h'),
                      type: 'bar',
                      stack: 'total',
                      data: chartData.map(d => d.overtime),
                      itemStyle: { color: getBlueShade(0) },
                      barMaxWidth: 40
                    },
                    {
                      name: t('due_h'),
                      type: 'bar',
                      stack: 'total',
                      data: chartData.map(d => d.due),
                      itemStyle: { color: BLUE_SHADES.dark3 },
                      barMaxWidth: 40
                    }
                  ]
                }}
                style={{ height: 320, minWidth: '100%' }}
              />
            </div>
          </div>
        </div>

        <DailySummaryTable data={chartData} />
      </div>
    </div>
  );
}